import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCapture } from "@/context/CaptureContext";
import { MODULE_ICONS, Home, Inbox, Search, Settings, Trash2, LogOut, Compass, Plus, Grid3x3, X } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import api from "@/lib/api";

function Wordmark({ small }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="grid place-items-center h-9 w-9 rounded-lg" style={{ background: "linear-gradient(135deg,#2563EB,#0EA5E9)" }}>
        <Compass size={20} className="text-white" />
      </div>
      {!small && (
        <div className="leading-none">
          <div className="font-display font-extrabold text-white text-lg tracking-tight">
            DockLinc <span className="text-sky-400">C⁶</span>
          </div>
          <div className="text-[10px] font-mono-label text-slate-500 mt-0.5">Your World. Connected.</div>
        </div>
      )}
    </div>
  );
}

function NavRow({ to, icon: Icon, color, label, badge, onClick, testid }) {
  const base = "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors";
  const content = (active) => (
    <>
      <span className="grid place-items-center h-7 w-7 rounded-md shrink-0"
            style={{ background: active ? color : "rgba(148,163,184,0.1)", color: active ? "#fff" : color }}>
        <Icon size={15} />
      </span>
      <span className="flex-1" style={{ color: active ? "#F8FAFC" : "#94A3B8" }}>{label}</span>
      {badge > 0 && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ background: "#F59E0B" }}>{badge}</span>
      )}
    </>
  );
  if (onClick) {
    return (
      <button data-testid={testid} onClick={onClick} className={base + " w-full"} style={{ background: "transparent" }}>
        {content(false)}
      </button>
    );
  }
  return (
    <NavLink to={to} end={to === "/"} data-testid={testid}
      className={({ isActive }) => base}
      style={({ isActive }) => ({ background: isActive ? "rgba(37,99,235,0.12)" : "transparent" })}>
      {({ isActive }) => content(isActive)}
    </NavLink>
  );
}

function SidebarContent({ modules, inboxCount, collections, openCapture, onNavigate, logout, user }) {
  return (
    <div className="flex flex-col h-full dl-scroll" style={{ background: "var(--dl-sidebar)" }}>
      <div className="p-5 pb-3"><Wordmark /></div>
      <nav className="px-3 space-y-1 flex-1 overflow-y-auto dl-scroll">
        <NavRow to="/" icon={Home} color="#2563EB" label="Home" testid="sidebar-home-link" />
        <div className="pt-3 pb-1 px-3 text-[10px] font-mono-label text-slate-600">The Six Cs</div>
        {modules.map((m) => m.key === "capture" ? (
          <NavRow key={m.key} onClick={() => { openCapture(); onNavigate && onNavigate(); }}
            icon={MODULE_ICONS[m.icon] || Grid3x3} color={m.color} label={m.label} testid="sidebar-capture-btn" />
        ) : (
          <NavRow key={m.key} to={m.route} icon={MODULE_ICONS[m.icon] || Grid3x3}
            color={m.color} label={m.label} badge={m.key === "collect" ? inboxCount : 0}
            testid={`sidebar-${m.key}-link`} />
        ))}
        <div className="pt-3 pb-1 px-3 text-[10px] font-mono-label text-slate-600">Collections</div>
        {collections.slice(0, 8).map((c) => (
          <NavLink key={c.id} to={`/collections/${c.id}`} onClick={onNavigate}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-white/5"
            data-testid={`sidebar-collection-${c.id}`}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
            <span className="truncate flex-1">{c.name}</span>
            <span className="text-[10px] text-slate-600">{c.item_count ?? ""}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t space-y-1" style={{ borderColor: "var(--dl-border)" }}>
        <NavRow to="/settings" icon={Settings} color="#64748B" label="Settings" testid="sidebar-settings-link" />
        <NavRow to="/trash" icon={Trash2} color="#64748B" label="Trash" testid="sidebar-trash-link" />
        <div className="flex items-center gap-3 px-3 pt-2">
          <div className="h-8 w-8 rounded-full grid place-items-center text-xs font-semibold text-white" style={{ background: "#2563EB" }}>
            {(user?.name || "U").slice(0, 1)}
          </div>
          <div className="flex-1 leading-tight">
            <div className="text-xs text-white font-medium truncate">{user?.name}</div>
            <div className="text-[10px] text-slate-500 capitalize">{user?.role}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-slate-500 hover:text-white"><LogOut size={16} /></button>
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, modules, logout } = useAuth();
  const { openCapture } = useCapture();
  const navigate = useNavigate();
  const location = useLocation();
  const [inboxCount, setInboxCount] = useState(0);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState("");
  const [mobileMenu, setMobileMenu] = useState(false);

  const refreshChrome = () => {
    api.get("/home").then((r) => setInboxCount(r.data.stats.to_catalog)).catch(() => {});
    api.get("/collections").then((r) => setCollections(r.data.collections)).catch(() => {});
  };
  useEffect(() => { refreshChrome(); }, [location.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--dl-bg)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[260px] shrink-0 border-r flex-col h-screen sticky top-0" style={{ borderColor: "var(--dl-border)" }}>
        <SidebarContent modules={modules} inboxCount={inboxCount} collections={collections} openCapture={openCapture} logout={logout} user={user} />
      </aside>

      {/* Mobile menu sheet */}
      <Sheet open={mobileMenu} onOpenChange={setMobileMenu}>
        <SheetContent side="left" className="p-0 w-[280px] border-r" style={{ borderColor: "var(--dl-border)" }}>
          <SidebarContent modules={modules} inboxCount={inboxCount} collections={collections} openCapture={openCapture} onNavigate={() => setMobileMenu(false)} logout={logout} user={user} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 flex items-center gap-3 px-4 lg:px-6 h-16 border-b backdrop-blur-xl"
          style={{ borderColor: "var(--dl-border)", background: "rgba(7,11,20,0.8)" }}>
          <button className="lg:hidden text-slate-300" onClick={() => setMobileMenu(true)} data-testid="mobile-menu-btn"><Grid3x3 size={22} /></button>
          <form onSubmit={submitSearch} className="flex-1 max-w-xl relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search captures, places, people…"
              className="pl-9 bg-black/30 border-0 ring-1 ring-inset ring-white/10 focus-visible:ring-sky-500" />
          </form>
          <Button data-testid="header-capture-btn" onClick={() => openCapture()} className="gap-2 capture-glow hidden sm:flex" style={{ background: "#2563EB" }}>
            <Plus size={16} /> Capture Now
          </Button>
        </header>

        <main className="flex-1 p-4 lg:p-7 pb-24 lg:pb-7 dl-grid-bg">
          <Outlet context={{ refreshChrome }} />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 h-16 flex items-center justify-around border-t backdrop-blur-xl"
        style={{ borderColor: "var(--dl-border)", background: "rgba(11,17,32,0.95)" }}>
        <MobileTab to="/" icon={Home} label="Home" />
        <MobileTab to="/collect" icon={Inbox} label="Collect" badge={inboxCount} />
        <button onClick={() => openCapture()} data-testid="mobile-capture-btn" className="grid place-items-center -mt-6 h-14 w-14 rounded-full capture-glow" style={{ background: "#2563EB" }}>
          <Plus size={26} className="text-white" />
        </button>
        <MobileTab to="/catalog" icon={Search} label="Catalog" />
        <button onClick={() => setMobileMenu(true)} className="flex flex-col items-center gap-0.5 text-slate-400" data-testid="mobile-more-btn">
          <Grid3x3 size={20} /><span className="text-[10px]">More</span>
        </button>
      </nav>
    </div>
  );
}

function MobileTab({ to, icon: Icon, label, badge }) {
  return (
    <NavLink to={to} end={to === "/"} className="flex flex-col items-center gap-0.5 relative"
      style={({ isActive }) => ({ color: isActive ? "#60A5FA" : "#94A3B8" })}>
      <Icon size={20} />
      <span className="text-[10px]">{label}</span>
      {badge > 0 && <span className="absolute -top-1 right-2 h-4 min-w-4 px-1 rounded-full text-[9px] grid place-items-center text-white" style={{ background: "#F59E0B" }}>{badge}</span>}
    </NavLink>
  );
}
